import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getInventoryItems,
  getInventorySettings,
  getOrders,
  getOrderConsumptions,
  listRestaurants,
} from '../../services/api';
import websocket from '../../services/websocket';
import { useAuth } from '../../context/AuthContext';
import { issuesCount } from '../../utils/consumptionIssues';
import {
  eventRestaurantId,
  groupLowStockAlerts,
  lowStockNames,
} from '../../utils/inventoryConsolidatedAlerts';
import { normalizeRestaurantList } from '../../utils/inventoryTransfers';
import { storage } from '../../utils/storage';
import './DockWidgets.css';

const REALTIME_TTL_MS = 30000;

// Marca en la lista periódica el lugar de las alertas de stock bajo (se agrupan por local al pintar)
const LOW_STOCK_SLOT = { id: 'low-stock-slot', slot: true };

const AlertsWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  // RES-43: stock bajo agrupado por local. Periódico = local seleccionado; realtime = LOW_STOCK
  const [periodicLowStock, setPeriodicLowStock] = useState([]);
  const [realtimeLowStock, setRealtimeLowStock] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Los handlers del WebSocket se registran una sola vez: leen permisos y usuario por ref.
  // null = todavía no se preguntó (ver canReviewSuggestions).
  const canReviewSuggestionsRef = useRef(null);
  const userIdRef = useRef(null);
  userIdRef.current = user?.userId || null;
  const restaurantNamesRef = useRef(new Map());

  const selectedRestaurantId = storage.get('selectedRestaurantId');

  useEffect(() => {
    loadAlerts();

    // Connect WebSocket and listen for waiter calls and bill requests
    const restaurantId = storage.get('selectedRestaurantId');

    // RES-43: nombres de los locales, para agrupar el stock bajo por local. Si falla, sin nombres.
    listRestaurants()
      .then((data) => setRestaurants(normalizeRestaurantList(data)))
      .catch(() => {});

    // Alerta en tiempo real: máx. 3, se quita sola a los 30 s. Misma id → se reemplaza, no se apila.
    const pushRealtimeAlert = (alert) => {
      setRealtimeAlerts((prev) => [alert, ...prev.filter((a) => a.id !== alert.id)].slice(0, 3));
      setTimeout(() => {
        setRealtimeAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      }, REALTIME_TTL_MS);
    };

    const handleWaiterCall = (data) => {
      console.log('[AlertsWidget] Waiter call received:', data);
      pushRealtimeAlert({
        id: `waiter-${Date.now()}`,
        type: 'info',
        icon: 'room_service',
        messageKey: 'dock.waiterCall',
        messageDefault: 'Mesa {{table}} solicita asistencia',
        messageParams: { table: data.data.tableNumber },
        timestamp: Date.now(),
        action: () => navigate('/orders'),
      });
    };

    const handleBillRequest = (data) => {
      console.log('[AlertsWidget] Bill request received:', data);
      pushRealtimeAlert({
        id: `bill-${Date.now()}`,
        type: 'warning',
        icon: 'receipt',
        messageKey: 'dock.billRequest',
        messageDefault: 'Mesa {{table}} solicita la cuenta',
        messageParams: { table: data.data.tableNumber },
        timestamp: Date.now(),
        action: () => navigate('/orders'),
      });
    };

    // RES-19: alertas de inventario que manda el backend por WebSocket (movimiento manual,
    // edición del insumo, venta por receta o chequeo diario de vencimientos).
    // RES-43: el stock bajo se agrupa por local (el del evento o, si no viene, el seleccionado).
    const handleLowStock = (data) => {
      const payload = data?.data || {};
      const entry = {
        restaurantId: eventRestaurantId(data, restaurantId),
        itemId: payload.itemId || payload.itemName,
        name: payload.itemName,
      };
      const sameEntry = (e) => e.restaurantId === entry.restaurantId && e.itemId === entry.itemId;
      setRealtimeLowStock((prev) => [entry, ...prev.filter((e) => !sameEntry(e))]);
      setTimeout(() => {
        setRealtimeLowStock((prev) => prev.filter((e) => !sameEntry(e)));
      }, REALTIME_TTL_MS);
      loadAlerts();
    };

    const handleExpirationWarning = (data) => {
      const payload = data?.data || {};
      pushRealtimeAlert({
        id: `exp-${payload.itemId || payload.itemName}`,
        type: 'danger',
        icon: 'event_busy',
        messageKey: 'dock.expiringSoon',
        messageDefault: 'Vence pronto: {{name}}',
        messageParams: { name: payload.itemName },
        timestamp: Date.now(),
        action: () => navigate('/inventory'),
      });
      loadAlerts();
    };

    // Permiso de revisar sugerencias (`can.suggestionsReview` = administra toda la organización).
    // Se pregunta recién con el primer evento: GET /inventory/settings tiene feature gate y, pedido
    // al montar el dock en un plan sin inventario, abriría el modal de upgrade en cada pantalla.
    const canReviewSuggestions = async () => {
      if (canReviewSuggestionsRef.current === null) {
        try {
          const settings = await getInventorySettings();
          canReviewSuggestionsRef.current = settings?.can?.suggestionsReview === true;
        } catch {
          canReviewSuggestionsRef.current = false;
        }
      }
      return canReviewSuggestionsRef.current;
    };

    // RES-46: una sucursal sugirió un insumo. Llega a todos los locales; solo lo ve quien
    // administra la organización.
    const handleSuggestion = async (data) => {
      const payload = data?.data || {};
      if (payload.audience === 'org_admins' && !(await canReviewSuggestions())) return;
      const restaurant = restaurantNamesRef.current.get(payload.restaurantId);
      pushRealtimeAlert({
        id: `suggestion-${payload.suggestionId || payload.name}`,
        type: 'info',
        icon: 'lightbulb',
        messageKey: restaurant
          ? 'alerts.inventorySuggestion.createdAt'
          : 'alerts.inventorySuggestion.created',
        messageDefault: restaurant
          ? '{{restaurant}} sugirió un insumo: {{name}}'
          : 'Sugerencia de insumo: {{name}}',
        messageParams: { name: payload.name, restaurant },
        timestamp: Date.now(),
        action: () => navigate('/inventory/suggestions'),
      });
    };

    // RES-46: revisaron una sugerencia. Solo le interesa a quien la hizo.
    const handleSuggestionReviewed = (data) => {
      const payload = data?.data || {};
      if (!payload.requestedBy || payload.requestedBy !== userIdRef.current) return;
      const approved = payload.status === 'approved';
      pushRealtimeAlert({
        id: `suggestion-reviewed-${payload.suggestionId || payload.name}`,
        type: approved ? 'info' : 'warning',
        icon: approved ? 'task_alt' : 'block',
        messageKey: approved
          ? 'alerts.inventorySuggestion.approved'
          : 'alerts.inventorySuggestion.rejected',
        messageDefault: approved
          ? 'Aprobaron tu sugerencia: {{name}}'
          : 'Rechazaron tu sugerencia: {{name}}',
        messageParams: { name: payload.name },
        timestamp: Date.now(),
        action: () => navigate(approved ? '/inventory' : '/inventory/suggestions'),
      });
    };

    if (restaurantId) {
      websocket.connect(restaurantId);
      websocket.on('WAITER_CALL', handleWaiterCall);
      websocket.on('BILL_REQUEST', handleBillRequest);
      websocket.on('LOW_STOCK', handleLowStock);
      websocket.on('EXPIRATION_WARNING', handleExpirationWarning);
      websocket.on('INVENTORY_SUGGESTION', handleSuggestion);
      websocket.on('INVENTORY_SUGGESTION_REVIEWED', handleSuggestionReviewed);
    }

    const interval = setInterval(loadAlerts, 120000); // Refresh inventory alerts every 2 minutes

    return () => {
      clearInterval(interval);
      websocket.off('WAITER_CALL', handleWaiterCall);
      websocket.off('BILL_REQUEST', handleBillRequest);
      websocket.off('LOW_STOCK', handleLowStock);
      websocket.off('EXPIRATION_WARNING', handleExpirationWarning);
      websocket.off('INVENTORY_SUGGESTION', handleSuggestion);
      websocket.off('INVENTORY_SUGGESTION_REVIEWED', handleSuggestionReviewed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAlerts = async () => {
    try {
      setError(false);
      const alertsList = [];

      // Check inventory for low stock and expiring items
      const inventoryData = await getInventoryItems();
      const items = inventoryData.items || [];

      // Delivered but unpaid orders — revenue risk
      try {
        const ordersData = await getOrders();
        const ordersList = ordersData.orders || ordersData || [];
        ordersList.forEach((order) => {
          if (order.status === 'delivered' && order.paymentStatus !== 'paid') {
            alertsList.push({
              id: `unpaid-${order.orderId}`,
              type: 'danger',
              icon: 'warning',
              messageKey: 'orders.deliveredUnpaidAlert',
              messageDefault: 'Pedido {{id}} entregado sin cobrar',
              messageParams: { id: order.orderId?.slice(-6) },
              action: () => navigate('/orders'),
            });
          }
        });
      } catch (ordersErr) {
        console.error('Error loading orders for alerts:', ordersErr);
      }

      // Low stock alerts. El insumo expone `stock`; con `currentStock` (que la API nunca
      // devolvió) la comparación daba false y esta alerta no salía (RES-19).
      // RES-43: van en una sola alerta por local, que se arma al pintar (ver LOW_STOCK_SLOT).
      const lowStock = items
        .filter((item) => {
          const stock = item.stock ?? item.currentStock;
          return stock !== undefined && stock <= item.minStock;
        })
        .map((item) => ({ itemId: item.itemId, name: item.name }));
      setPeriodicLowStock(lowStock);
      if (lowStock.length > 0) alertsList.push(LOW_STOCK_SLOT);

      // Expiring soon alerts (within 3 days)
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

      items.forEach((item) => {
        if (item.expirationDate) {
          const parsed = new Date(item.expirationDate);
          const expDate = new Date(
            parsed.getUTCFullYear(),
            parsed.getUTCMonth(),
            parsed.getUTCDate()
          );
          if (expDate <= threeDaysFromNow && expDate >= new Date()) {
            alertsList.push({
              id: `exp-${item.itemId}`,
              type: 'danger',
              icon: 'event_busy',
              messageKey: 'dock.expiringSoon',
              messageDefault: 'Vence pronto: {{name}}',
              messageParams: { name: item.name },
              action: () => navigate('/inventory'),
            });
          }
        }
      });

      // Consumos con problemas (B6 / RES-34): pedidos cuyo descuento de stock quedó incompleto.
      // Va al final y nunca rompe el resto de las alertas si el endpoint no está disponible.
      // RES-65: iba con `unshift`, contra lo que decía este comentario, así que este warning
      // desplazaba del dock a una alerta de peligro (pedido entregado sin cobrar) al cortar en 5.
      try {
        const consumptions = await getOrderConsumptions({ status: 'partial', limit: 50 });
        const withIssues = issuesCount(consumptions?.items);
        if (withIssues > 0) {
          alertsList.push({
            id: 'consumption-issues',
            type: 'warning',
            icon: 'report_problem',
            messageKey: 'dock.consumptionIssues',
            messageDefault: '{{count}} consumo(s) con problemas',
            messageParams: { count: withIssues },
            action: () => navigate('/inventory/movements'),
          });
        }
      } catch {
        // sin inventario habilitado o endpoint caído: no es una alerta
      }

      setAlerts(alertsList.slice(0, 5)); // Max 5 alerts
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const restaurantNames = useMemo(
    () => new Map(restaurants.map((r) => [r.restaurantId, r.name])),
    [restaurants]
  );
  restaurantNamesRef.current = restaurantNames;

  // RES-43: una alerta de stock bajo por local. Con un solo local no se antepone el nombre.
  const lowStockGroups = useMemo(() => {
    const isChain = restaurants.length > 1;
    return groupLowStockAlerts({
      realtime: realtimeLowStock,
      periodic: periodicLowStock,
      selectedRestaurantId,
    }).map((group) => {
      const restaurantName = restaurantNames.get(group.restaurantId);
      const otherLocal = Boolean(group.restaurantId) && group.restaurantId !== selectedRestaurantId;
      const names = lowStockNames(group.names);
      const withLocal = restaurantName && (isChain || otherLocal);
      return {
        id: `stock-${group.restaurantId || 'current'}`,
        realtime: group.realtime,
        type: 'warning',
        icon: 'inventory_2',
        messageKey: withLocal ? 'alerts.inventoryLowStock.byRestaurant' : 'dock.lowStock',
        messageDefault: withLocal
          ? '{{restaurant}} · Stock bajo: {{name}}'
          : 'Stock bajo: {{name}}',
        messageParams: withLocal
          ? { restaurant: restaurantName, name: names, count: group.count }
          : { name: names },
        // Otro local: el detalle está en el consolidado (el inventario muestra el seleccionado)
        action: () => navigate(otherLocal ? '/inventory/consolidated' : '/inventory'),
      };
    });
  }, [
    realtimeLowStock,
    periodicLowStock,
    selectedRestaurantId,
    restaurants,
    restaurantNames,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="widget-loading">
        <span className="material-icons spinning">sync</span>
      </div>
    );
  }

  // Realtime primero; el stock bajo con eventos en vivo va arriba, el resto en su lugar de la lista.
  const liveStock = lowStockGroups.filter((g) => g.realtime);
  const restStock = lowStockGroups.filter((g) => !g.realtime);
  const periodic = alerts.flatMap((alert) => (alert.slot ? restStock : [alert]));
  const seenIds = new Set();
  const allAlerts = [...realtimeAlerts, ...liveStock, ...periodic]
    .filter((alert) => {
      if (seenIds.has(alert.id)) return false;
      seenIds.add(alert.id);
      return true;
    })
    .slice(0, 5);

  const hasRealtime = realtimeAlerts.length > 0 || liveStock.length > 0;

  // Show error only if no realtime alerts are available as fallback
  if (error && !hasRealtime) {
    return (
      <div className="widget-error">
        <span className="material-icons">cloud_off</span>
        <span>{t('dock.loadError', 'Error al cargar datos')}</span>
        <button type="button" className="widget-action-sm" onClick={loadAlerts}>
          {t('common.retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  return (
    <div className="alerts-widget" aria-live="polite" aria-relevant="additions removals">
      {allAlerts.length === 0 ? (
        <div className="widget-empty success">
          <span className="material-icons">check_circle</span>
          <span>{t('dock.noAlerts', 'Sin alertas')}</span>
        </div>
      ) : (
        <div className="alerts-list" role="log">
          {allAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`alert-item alert-${alert.type}`}
              role={alert.type === 'danger' ? 'alert' : undefined}
              onClick={alert.action}
            >
              <span className="material-icons alert-icon">{alert.icon}</span>
              <span className="alert-message">
                {t(alert.messageKey, alert.messageDefault, alert.messageParams)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsWidget;
